import {
  Component,
  forwardRef,
  HostListener,
  Input,
  OnInit
  } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ItemPipe } from '../pipes/item.pipe';
import { SelectableItem } from '../models/selectable-item';
import { SelectOption } from '../models/select-option';
import { SelectService } from '../services/select.service';
import { TreeSelectDefaultOptions } from '../models/tree-select-default-options';
import { ExpandMode } from '../models/expand-mode';

// tslint:disable-next-line:no-empty
const /** @type {?} */ noop = () => {};

export const /** @type {?} */ CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line:no-forward-ref
  useExisting: forwardRef(() => TreeSelectComponent),
  multi: true
};
export class TreeSelectComponent implements ControlValueAccessor {
public onTouchedCallback: () => void = noop;
public showMoreLink = false;
public moreLoaded = false;
public disabled = false;
public placeholder = '';
public filterPlaceholder = 'Type here for filtering items...';
public allowFilter = true;
public filterFunction: (item, filter) => boolean;
private _isOpen = false;
private onChangeCallback: (_: any) => void = noop;
private haveFocus = false;
private inputFocus = false;
/**
 * @param {?} value
 * @return {?}
 */
public set items(value: any[]) {
    this.svc.setItems(value);
  }
/**
 * @param {?} value
 * @return {?}
 */
public set idField(value: string) {
    this.svc.setConfiguration((opt) => opt.idProperty = value, true);
  }
/**
 * @param {?} value
 * @return {?}
 */
public set textField(value: string) {
    this.svc.setConfiguration((opt) => opt.textProperty = value, true);
  }
/**
 * @param {?} value
 * @return {?}
 */
public set allowParentSelection(value: boolean) {
    this.svc.setConfiguration((opt) => opt.allowParentSelection = value, true);
  }
/**
 * @return {?}
 */
public get allowParentSelection(): boolean {
    return this.svc.Configuration.allowParentSelection;
  }
/**
 * @param {?} value
 * @return {?}
 */
public set restructureWhenChildSameName(value: boolean) {
    this.svc.setConfiguration((opt) => opt.restructureWhenChildSameName = value, true);
  }
/**
 * @return {?}
 */
public get restructureWhenChildSameName(): boolean {
    return this.svc.Configuration.restructureWhenChildSameName;
  }
/**
 * @param {?} value
 * @return {?}
 */
public set childrenField(value: string) {
    this.svc.setConfiguration((opt) => opt.childProperty = value, true);
  }
/**
 * @param {?} value
 * @return {?}
 */
public set multiple(value: boolean) {
    this.svc.setConfiguration((opt) => opt.allowMultiple = value, true);
  }
/**
 * @return {?}
 */
public get multiple(): boolean {
    return this.svc.Configuration.allowMultiple;
  }
/**
 * @param {?} value
 * @return {?}
 */
public set filterCaseSensitive(value: boolean) {
    this.svc.setConfiguration((opt) => opt.filterCaseSensitive = value, true);
  }
/**
 * @return {?}
 */
public get filterCaseSensitive(): boolean {
    return this.svc.Configuration.filterCaseSensitive;
  }
/**
 * @param {?} value
 * @return {?}
 */
public set expandMode(value: string) {
    this.svc.setConfiguration((opt) => opt.expandMode = value, true);
    this.svc.setExpand();
  }
/**
 * @return {?}
 */
public get expandMode(): string {
    return this.svc.Configuration.expandMode;
  }
/**
 * @param {?} value
 * @return {?}
 */
public set maxVisibleItemCount(value: number) {
    this.svc.setConfiguration((opt) => opt.maxVisibleItemCount = value, true);
  }
/**
 * @return {?}
 */
public get maxVisibleItemCount(): number {
    return this.svc.Configuration.maxVisibleItemCount;
  }
/**
 * @return {?}
 */
public get internalItems(): SelectableItem[] {
    return this.svc.getInternalItems() || [];
  }
/**
 * @return {?}
 */
public get selection(): SelectableItem[] {
    this.showMoreLink = (
      this.maxVisibleItemCount > 0 &&
      ((this.svc.getInternalSelection().length - this.maxVisibleItemCount) > 0)
    );
    return this.svc.getInternalSelection();
  }
/**
 * @return {?}
 */
public get filter(): string {
    return this.svc.Configuration.filter;
  }
/**
 * @param {?} value
 * @return {?}
 */
public set filter(value: string) {
    this.svc.setConfiguration((opt) => opt.filter = value, false);
    for (const /** @type {?} */ item of this.internalItems) {
      this.ProcessMatchFilterTreeItem(item, this.svc.Configuration.filter);
    }
    this.svc.setExpand();
  }
/**
 * @param {?} svc
 * @param {?} defaultOpts
 */
public constructor(
private svc: SelectService,
private defaultOpts: TreeSelectDefaultOptions
  ) {
    this.clickedOutside = this.clickedOutside.bind(this);

    this.svc.modelChanged$.subscribe((result) => {
      this.onChangeCallback(result);
    });
    this.maxVisibleItemCount = (defaultOpts.maxVisibleItemCount || 0);
    this.allowParentSelection = (
      (defaultOpts.allowParentSelection === undefined ||
       defaultOpts.allowParentSelection === null) ?
        true :
        defaultOpts.allowParentSelection
    );
    this.allowFilter = (
      (defaultOpts.allowFilter === undefined || defaultOpts.allowFilter === null) ?
        true :
        defaultOpts.allowFilter
    );
    this.filterCaseSensitive = (
      (defaultOpts.filterCaseSensitive === undefined || defaultOpts.filterCaseSensitive === null) ?
        false :
        defaultOpts.filterCaseSensitive
    );
    this.filterPlaceholder = (defaultOpts.filterPlaceholder || 'Type here for filtering items...');
    this.idField = (defaultOpts.idField || 'id');
    this.textField = (defaultOpts.textField || 'id');
    this.childrenField = (defaultOpts.childrenField || '');
    this.expandMode = (defaultOpts.expandMode || ExpandMode.None);
  }
/**
 * @param {?} $event
 * @return {?}
 */
public keyUp($event: any) {}
/**
 * @param {?} $event
 * @return {?}
 */
public toggle($event: any) {
    $event.preventDefault();
    this.haveFocus = true;
    this.svc.toggleOpen();
  }
/**
 * @param {?} $event
 * @param {?} item
 * @return {?}
 */
public removeItem($event: any, item: SelectableItem): void {
    $event.stopPropagation();
    this.svc.toggleItemSelection(item);
  }
/**
 * @return {?}
 */
public get isOpen(): boolean {
    return this.svc.Configuration.isOpen;
  }
/**
 * @return {?}
 */
public clickedOutside() {
    if (!this.inputFocus) {
      if (!this.haveFocus && this.isOpen || this.haveFocus && !this.isOpen) {
        this.onTouched();
      }
      this.haveFocus = false;
    }
  }
/**
 * @return {?}
 */
public onTouched() {
    this.svc.close();
    this.onTouchedCallback();
  }
/**
 * @return {?}
 */
public setInputFocus() {
    this.inputFocus = true;
  }
/**
 * @return {?}
 */
public setInputFocusOut() {
    this.inputFocus = false;
  }
/**
 * Write a new value to the element.
 * 
 * \@memberof TreeSelectComponent
 * @param {?} value
 * @return {?}
 */
public writeValue(value: any): void {
    this.svc.setSelection(value);
  }
/**
 * Set the function to be called when the control receives a change event.
 * 
 * \@memberof TreeSelectComponent
 * @param {?} fn
 * @return {?}
 */
public registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }
/**
 * Set the function to be called when the control receives a touch event.
 * 
 * \@memberof TreeSelectComponent
 * @param {?} fn
 * @return {?}
 */
public registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }
/**
 * This function is called when the control status changes to or from "DISABLED".
 * Depending on the value, it will enable or disable the appropriate DOM element.
 * 
 * \@memberof TreeSelectComponent
 * @param {?} isDisabled
 * @return {?}
 */
public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
/**
 * This finction is called when user click on show more link.
 * 
 * \@memberof TreeSelectComponent
 * @param {?} $event
 * @return {?}
 */
public loadMore($event: any) {
    $event.stopPropagation();
    this.moreLoaded = !this.moreLoaded;
  }
/**
 * @param {?} tree
 * @param {?} filter
 * @return {?}
 */
private ProcessMatchFilterTreeItem(tree: SelectableItem, filter: string): boolean {
    let /** @type {?} */ result = false;
    if (tree && tree.children && tree.children.length > 0) {
      for (const /** @type {?} */ child of tree.children) {
        result = this.ProcessMatchFilterTreeItem(child, filter) || result;
      }
    }
	if(this.filterFunction && typeof (this.filterFunction) == 'function') {
		tree.matchFilter = this.filterFunction(tree, filter) || result;
		return tree.matchFilter;
	}

	if(typeof (tree.text) == 'object') {
		tree.matchFilter = false;
		return tree.matchFilter;
	}

    tree.matchFilter = this.filterCaseSensitive ?
                      (
                        tree.text.indexOf(filter) > -1 ||
                        result) :
                      (
                        tree.text.toLowerCase().indexOf(filter.toLowerCase()) > -1 ||
                        result
                      );
    return tree.matchFilter;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'tree-select',
  templateUrl: './tree-select.component.html',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, SelectService],
  styleUrls: ['./tree-select.component.scss']
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: SelectService, },
{type: TreeSelectDefaultOptions, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'disabled': [{ type: Input },],
'placeholder': [{ type: Input },],
'filterPlaceholder': [{ type: Input },],
'allowFilter': [{ type: Input },],
'filterFunction': [{ type: Input },],
'items': [{ type: Input },],
'idField': [{ type: Input },],
'textField': [{ type: Input },],
'allowParentSelection': [{ type: Input },],
'restructureWhenChildSameName': [{ type: Input },],
'childrenField': [{ type: Input },],
'multiple': [{ type: Input },],
'filterCaseSensitive': [{ type: Input },],
'expandMode': [{ type: Input },],
'maxVisibleItemCount': [{ type: Input },],
};
}

function TreeSelectComponent_tsickle_Closure_declarations() {
/** @type {?} */
TreeSelectComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TreeSelectComponent.ctorParameters;
/** @type {?} */
TreeSelectComponent.propDecorators;
/** @type {?} */
TreeSelectComponent.prototype.onTouchedCallback;
/** @type {?} */
TreeSelectComponent.prototype.showMoreLink;
/** @type {?} */
TreeSelectComponent.prototype.moreLoaded;
/** @type {?} */
TreeSelectComponent.prototype.disabled;
/** @type {?} */
TreeSelectComponent.prototype.placeholder;
/** @type {?} */
TreeSelectComponent.prototype.filterPlaceholder;
/** @type {?} */
TreeSelectComponent.prototype.allowFilter;
/** @type {?} */
TreeSelectComponent.prototype.filterFunction;
/** @type {?} */
TreeSelectComponent.prototype._isOpen;
/** @type {?} */
TreeSelectComponent.prototype.onChangeCallback;
/** @type {?} */
TreeSelectComponent.prototype.haveFocus;
/** @type {?} */
TreeSelectComponent.prototype.inputFocus;
/** @type {?} */
TreeSelectComponent.prototype.svc;
/** @type {?} */
TreeSelectComponent.prototype.defaultOpts;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
